import { globalAppCloseConfirmCopy } from "./copy.js";
import type { GlobalAppCloseLocale, GlobalAppCloseResult } from "./types.js";

type TelegramWebAppLike = {
  initData?: string;
  platform?: string;
  close?: () => void;
  showPopup?: (
    params: {
      title?: string;
      message: string;
      buttons?: Array<{ id?: string; type?: string; text: string }>;
    },
    callback?: (buttonId: string) => void,
  ) => void;
  showConfirm?: (message: string, callback?: (ok: boolean) => void) => void;
};

function getTelegramWebApp(): TelegramWebAppLike | null {
  try {
    return (
      (globalThis as unknown as { Telegram?: { WebApp?: TelegramWebAppLike } }).Telegram?.WebApp ??
      null
    );
  } catch {
    return null;
  }
}

export function isTelegramMiniAppContext(): boolean {
  const wa = getTelegramWebApp();
  if (!wa) return false;
  const initData = String(wa.initData ?? "").trim();
  if (initData.length > 0) return true;
  const platform = String(wa.platform ?? "").trim();
  return platform.length > 0 && platform !== "unknown";
}

export function confirmDirtyClose(locale: GlobalAppCloseLocale): Promise<boolean> {
  const copy = globalAppCloseConfirmCopy(locale);
  const wa = getTelegramWebApp();
  if (wa?.showPopup) {
    return new Promise((resolve) => {
      try {
        wa.showPopup?.(
          {
            title: copy.title,
            message: copy.message,
            buttons: [
              { id: "stay", type: "default", text: copy.stay },
              { id: "close", type: "destructive", text: copy.close },
            ],
          },
          (id) => resolve(id === "close"),
        );
      } catch {
        resolve(typeof window !== "undefined" ? window.confirm(`${copy.title}\n\n${copy.message}`) : false);
      }
    });
  }
  if (wa?.showConfirm) {
    return new Promise((resolve) => {
      try {
        wa.showConfirm?.(`${copy.title}\n${copy.message}`, (ok) => resolve(Boolean(ok)));
      } catch {
        resolve(typeof window !== "undefined" ? window.confirm(`${copy.title}\n\n${copy.message}`) : false);
      }
    });
  }
  if (typeof window !== "undefined") {
    return Promise.resolve(window.confirm(`${copy.title}\n\n${copy.message}`));
  }
  return Promise.resolve(false);
}

export function closeTelegramMiniApp(): boolean {
  const wa = getTelegramWebApp();
  if (typeof wa?.close === "function" && isTelegramMiniAppContext()) {
    wa.close();
    return true;
  }
  return false;
}

/**
 * Browser fallback without history loops.
 * 1) window.close when opener exists
 * 2) history.back once when safe depth exists
 * 3) otherwise caller handles UI fallback
 */
export function attemptBrowserWindowCloseOrBack(): "closed" | "backed" | "none" {
  if (typeof window === "undefined") return "none";
  try {
    if (window.opener && !window.opener.closed) {
      window.close();
      return "closed";
    }
  } catch {
    /* ignore */
  }
  try {
    if (window.history.length > 1) {
      window.history.back();
      return "backed";
    }
  } catch {
    /* ignore */
  }
  return "none";
}

export type CloseMiniAppInput = {
  /** Optional sync hook (e.g. arm post-order explicit close). Must not clear payment resume. */
  onBeforeClose?: () => void;
  /** Called when Telegram close is unavailable and window/history fallback failed. */
  onBrowserUiFallback?: () => void;
};

export type CloseMiniAppResult = "closed" | "browser_fallback";

/**
 * CUSTOMER-MINIAPP-131 — canonical Mini App close.
 * Telegram mobile + Telegram WebK share the same WebApp.close path.
 * Direct browser: window.close → history.back → visible UI fallback (never silent).
 */
export function closeMiniApp(input: CloseMiniAppInput = {}): CloseMiniAppResult {
  try {
    input.onBeforeClose?.();
  } catch {
    /* never block close */
  }
  if (closeTelegramMiniApp()) {
    return "closed";
  }
  const browser = attemptBrowserWindowCloseOrBack();
  if (browser === "closed" || browser === "backed") {
    return "browser_fallback";
  }
  try {
    input.onBrowserUiFallback?.();
  } catch {
    /* ignore */
  }
  return "browser_fallback";
}

export type RequestGlobalAppCloseInput = {
  locale: GlobalAppCloseLocale;
  dirty: boolean;
  /** Optional sync hook (e.g. arm post-order explicit close). Must not clear payment resume. */
  onBeforeClose?: () => void;
  /** Called when Telegram close is unavailable and window/history fallback failed. */
  onBrowserUiFallback?: () => void;
};

export async function requestGlobalAppClose(
  input: RequestGlobalAppCloseInput,
): Promise<GlobalAppCloseResult> {
  if (input.dirty) {
    const ok = await confirmDirtyClose(input.locale);
    if (!ok) return "cancelled";
  }
  return closeMiniApp({
    ...(input.onBeforeClose ? { onBeforeClose: input.onBeforeClose } : {}),
    ...(input.onBrowserUiFallback ? { onBrowserUiFallback: input.onBrowserUiFallback } : {}),
  });
}
